import { motion } from "framer-motion";
import type { ReactNode } from "react";

/**
 * Beyond Sure — StatCard primitive (P0).
 *
 * The KPI tile used in dashboards (My Policies, Family hub, OCR Pro
 * leaderboard, admin reports). Replaces the per-screen StatCard that
 * currently lives inline in MyPoliciesScreen.tsx.
 *
 * Visual rules:
 *   - Label: caps eyebrow, ink-40, 10px
 *   - Value: bold display, ink-100, 18-20px
 *   - Hint:  helper subline, ink-60, 11-11.5px
 *   - Warn tone: red border + ink for "Expiring 60d" style attention
 *
 * Hover lift is automatic — these tiles are calm but pickable.
 */
type Tone = "calm" | "warn" | "success" | "info";

interface StatCardProps {
  label: string;
  value: string | number;
  hint?: string;
  tone?: Tone;
  /** Optional leading icon component (rendered before the value, small). */
  icon?: ReactNode;
  /** Optional click handler — turns the tile into a button. */
  onClick?: () => void;
  /** data-testid forwarded for Puppeteer / e2e. */
  "data-testid"?: string;
  className?: string;
}

export function StatCard({
  label,
  value,
  hint,
  tone = "calm",
  icon,
  onClick,
  className,
  ...rest
}: StatCardProps) {
  const borderClass =
    tone === "warn"
      ? "border-[rgb(214_64_64_/_0.32)]"
      : tone === "success"
      ? "border-[var(--color-brand)]/30"
      : tone === "info"
      ? "border-[var(--color-azure)]/30"
      : "border-[var(--color-ink-08)]";

  const hintColor =
    tone === "warn"
      ? "text-[#9a3a3a]"
      : tone === "success"
      ? "text-[var(--color-brand-deep)]"
      : tone === "info"
      ? "text-[var(--color-azure)]"
      : "text-[var(--color-ink-60)]";

  const Wrap = onClick ? motion.button : motion.div;
  const interactiveProps = onClick
    ? {
        type: "button" as const,
        onClick,
        whileHover: { y: -2, transition: { duration: 0.15 } },
        whileTap: { scale: 0.98 },
      }
    : {
        whileHover: { y: -2, transition: { duration: 0.15 } },
      };

  return (
    <Wrap
      className={[
        "block w-full text-left",
        "rounded-[12px] p-3 sm:p-4",
        "bg-[rgb(255_255_255_/_0.92)] backdrop-blur-sm",
        "border", borderClass,
        "shadow-[0_1px_6px_-4px_rgba(20,75,51,0.18)]",
        "transition-colors duration-[var(--duration-fast)]",
        onClick ? "cursor-pointer hover:bg-[var(--color-paper)]" : "",
        className ?? "",
      ].join(" ")}
      {...interactiveProps}
      {...rest}
    >
      <div className="flex items-center gap-1.5">
        {icon ? <span className="text-[var(--color-ink-40)] shrink-0">{icon}</span> : null}
        <p className="bsure-caps text-[9.5px] sm:text-[10px] font-semibold tracking-[0.14em] text-[var(--color-ink-40)] truncate">
          {label}
        </p>
      </div>
      <p className="mt-1 text-[18px] sm:text-[20px] leading-[1.15] font-bold text-[var(--color-ink)] truncate">
        {value}
      </p>
      {hint ? (
        <p className={["mt-0.5 text-[11px] sm:text-[11.5px] truncate", hintColor].join(" ")}>
          {hint}
        </p>
      ) : null}
    </Wrap>
  );
}

export default StatCard;
